import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';

import { api } from '../api';
import { tagMatches } from './util';
import type {
  Plan,
  PlanSettings,
  Rhythm,
  RhythmDraft,
  TaskList,
  VaultTask,
  WritableCalendar
} from './types';

export function usePlan(from: Date, to: Date) {
  const f = format(from, 'yyyy-MM-dd');
  const t = format(to, 'yyyy-MM-dd');
  return useQuery({
    queryKey: ['plan', f, t],
    queryFn: () => api.get<Plan>(`/api/plan?from=${f}&to=${t}`),
    // Proposals slide forward as time passes, so the plan refreshes itself.
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
    placeholderData: (prev) => prev
  });
}

export function useRhythms() {
  return useQuery({
    queryKey: ['plan-rhythms'],
    queryFn: () => api.get<Rhythm[]>('/api/plan/rhythms')
  });
}

export function useTasks() {
  return useQuery({
    queryKey: ['plan-tasks'],
    queryFn: () => api.get<TaskList>('/api/plan/tasks'),
    // Tasks change in Obsidian with nothing telling Rialto.
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
    staleTime: 0
  });
}

export function useWritableCalendars() {
  return useQuery({
    queryKey: ['plan-calendars'],
    queryFn: () => api.get<WritableCalendar[]>('/api/plan/calendars'),
    staleTime: 5 * 60_000
  });
}

export function tasksFor(tasks: VaultTask[] | undefined, rhythm: Rhythm | undefined): VaultTask[] {
  if (!tasks || !rhythm || rhythm.task_tags.length === 0) return [];
  return tasks.filter((t) => tagMatches(t.tags, rhythm.task_tags));
}

function useInvalidate() {
  const qc = useQueryClient();
  return (...keys: string[]) => {
    for (const k of keys) qc.invalidateQueries({ queryKey: [k] });
  };
}

export function usePlanActions() {
  const invalidate = useInvalidate();
  const plan = () => invalidate('plan');

  const log = useMutation({
    mutationFn: (b: { rhythm_id: string; start: string; minutes: number; pin_id?: string }) =>
      api.post<{ id: string }>('/api/plan/log', b),
    onSettled: plan
  });
  const unlog = useMutation({
    mutationFn: (id: string) => api.delete(`/api/plan/log/${id}`),
    onSettled: plan
  });
  const pin = useMutation({
    mutationFn: (b: { rhythm_id: string; start: string; end: string }) =>
      api.post<{ id: string }>('/api/plan/pins', b),
    onSettled: plan
  });
  const movePin = useMutation({
    mutationFn: ({ id, start, end }: { id: string; start: string; end: string }) =>
      api.patch(`/api/plan/pins/${id}`, { start, end }),
    onSettled: plan
  });
  const unpin = useMutation({
    mutationFn: (id: string) => api.delete(`/api/plan/pins/${id}`),
    onSettled: plan
  });
  const skip = useMutation({
    mutationFn: (b: { rhythm_id: string; day: string; skipped: boolean }) =>
      api.put('/api/plan/skips', b),
    onSettled: plan
  });
  const commit = useMutation({
    mutationFn: (b: {
      rhythm_id: string;
      start: string;
      end: string;
      calendar_id?: string | null;
      title?: string;
      pin_id?: string;
    }) => api.post<{ id: string }>('/api/plan/commit', b),
    onSettled: () => invalidate('plan', 'events')
  });
  return { log, unlog, pin, movePin, unpin, skip, commit };
}

export function useTaskActions() {
  const invalidate = useInvalidate();
  const done = useMutation({
    mutationFn: (b: { key: string; done: boolean }) => api.post('/api/plan/tasks/done', b),
    onSettled: () => invalidate('plan-tasks')
  });
  const add = useMutation({
    mutationFn: (b: { text: string; tag: string; rhythm_id?: string; day?: string }) =>
      api.post<{ key: string }>('/api/plan/tasks', b),
    onSettled: () => invalidate('plan-tasks', 'plan', 'daily-notes')
  });
  const assign = useMutation({
    mutationFn: (b: { key: string; text: string; rhythm_id: string | null; day: string | null }) =>
      api.put('/api/plan/tasks/assign', b),
    onSettled: () => invalidate('plan')
  });
  return { done, add, assign };
}

export function useRhythmActions() {
  const qc = useQueryClient();
  const settle = () => {
    qc.invalidateQueries({ queryKey: ['plan-rhythms'] });
    qc.invalidateQueries({ queryKey: ['plan'] });
  };
  const create = useMutation({
    mutationFn: (b: RhythmDraft) => api.post<Rhythm>('/api/plan/rhythms', b),
    onSettled: settle
  });
  const update = useMutation({
    mutationFn: ({ id, ...patch }: Partial<Rhythm> & { id: string }) =>
      api.patch<Rhythm>(`/api/plan/rhythms/${id}`, patch),
    onMutate: ({ id, ...patch }) => {
      qc.setQueryData<Rhythm[]>(['plan-rhythms'], (old) =>
        old ? old.map((r) => (r.id === id ? { ...r, ...patch } : r)) : old
      );
    },
    onSettled: settle
  });
  const remove = useMutation({
    mutationFn: (id: string) => api.delete(`/api/plan/rhythms/${id}`),
    onSettled: settle
  });
  const reorder = useMutation({
    mutationFn: (ids: string[]) => api.put<Rhythm[]>('/api/plan/rhythms/order', { ids }),
    onMutate: (ids) => {
      qc.setQueryData<Rhythm[]>(['plan-rhythms'], (old) =>
        old
          ? ids
              .map((id, i) => {
                const r = old.find((x) => x.id === id);
                return r ? { ...r, position: i } : null;
              })
              .filter((r): r is Rhythm => r !== null)
          : old
      );
    },
    onSettled: settle
  });
  return { create, update, remove, reorder };
}

export function usePlanSettingsAction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (patch: Partial<PlanSettings>) => api.patch<PlanSettings>('/api/plan/settings', patch),
    onSettled: () => qc.invalidateQueries({ queryKey: ['plan'] })
  });
}
